import api from 'services/api';
import { enforce, num, nullable, object, runSchema, str } from 'validation';

/**
 * A single lock as returned by nap-backend `GET /locks/:id` (routes/locks.js,
 * which reuses the `getLocks` list query scoped to one id). Re-derived from the
 * backend source, NOT the `locksSlice` `Lock` type, which drifted: this endpoint
 * does NOT enrich `folder_path` (only the list / POST / PUT handlers do), and it
 * DOES return `limit_allocation` (absent from the slice type). `zone_name` is
 * non-null (the zone FK is NOT NULL). Deeply readonly — validated wire data.
 *
 * This is the bare `<Entity>` — the canonical single-record read every other
 * lock shape (`LockRow`, `CreatedLock`, `UpdatedLock`) is described relative to.
 */
export interface Lock {
  readonly id: number;
  readonly name: string;
  readonly description: string | null;
  readonly folder_id: number | null;
  readonly zone_name: string;
  readonly limit: number;
  readonly limit_allocation: number;
  readonly created_at: string;
  readonly updated_at: string;
}

const lockSchema = object<Lock>({
  id: num(),
  name: str(),
  description: nullable(str()),
  folder_id: nullable(num()),
  zone_name: str(),
  limit: num(),
  limit_allocation: num(),
  created_at: str(),
  updated_at: str(),
});

/**
 * Fetch a single lock by id for the edit form when the list slice is empty
 * (e.g. launched from the Workspace). Boundary-validated (issue #757 step 3);
 * structural failure or a drifted identity (`id`) yields null so the caller
 * surfaces a "not found" instead of opening the modal on a degraded record.
 */
export const getLock = async (id: number): Promise<Lock | null> => {
  try {
    const res = await api.get(`/locks/${id}`);
    const result = runSchema(lockSchema, res.data, 'getLock');
    if (!result.ok) return null;
    return enforce(result.value, result.warnings, ['id']);
  } catch {
    return null;
  }
};
